<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import AssistantBasicSettingsForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantBasicSettingsForm.vue';
import AssistantSystemSettingsForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantSystemSettingsForm.vue';
import AssistantScheduleSettingsForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantScheduleSettingsForm.vue';
import AssistantControlItems from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantControlItems.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import ConfigWithAiDialog from 'dashboard/components-next/captain/pageComponents/assistant/ConfigWithAiDialog.vue';
import OnboardingChecklist from 'dashboard/components-next/captain/pageComponents/assistant/OnboardingChecklist.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { isAdmin } = useAdmin();

const isCaptainV2Enabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CAPTAIN_V2)
);
const route = useRoute();
const router = useRouter();
const store = useStore();

const deleteAssistantDialog = ref(null);
const configWithAiDialog = ref(null);
const isCloning = ref(false);

const handleConfigWithAi = () => configWithAiDialog.value?.open();

const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const assistants = useMapGetter('captainAssistants/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);

const controlItems = computed(() => {
  return [
    {
      name: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.TITLE'
      ),
      description: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.DESCRIPTION'
      ),
      routeName: 'captain_assistants_guardrails_index',
    },
    {
      name: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.TITLE'
      ),
      description: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.DESCRIPTION'
      ),
      routeName: 'captain_assistants_guidelines_index',
    },
    {
      name: 'Materiais',
      description:
        'Arquivos que o agente envia na conversa (prova social, demonstrativo, contrato).',
      routeName: 'captain_assistants_materials_index',
    },
    {
      name: 'Painel da IA',
      description:
        'Métricas do agente e os erros reportados pelos atendentes (correções).',
      routeName: 'captain_assistants_ai_panel_index',
      // Endpoints de métricas/feedbacks são admin-only — escondemos o item dos
      // não-admins para não levá-los a uma página em branco (a rota também é
      // restrita a admin, então um acesso direto cai no redirect de sem-acesso).
      adminOnly: true,
    },
    {
      name: 'Ações do agente',
      description:
        'Escolha quais ações o agente pode executar (FAQ, agendar, mover Kanban, enviar material, calcular economia, etc.).',
      routeName: 'captain_assistants_tool_selection_index',
    },
    {
      name: 'Campos da IA',
      description:
        'Escolha campos personalizados e diga quando a IA deve preenchê-los na conversa (ex.: valor da conta, distribuidora, interesse).',
      routeName: 'captain_assistants_field_rules_index',
    },
    {
      name: 'Regras de roteamento',
      description:
        'Troque o assistente automaticamente quando uma etiqueta é aplicada na conversa ou o card entra numa etapa do Kanban (encaminha para vendas/suporte).',
      routeName: 'captain_assistants_routing_rules_index',
    },
  ].filter(item => !item.adminOnly || isAdmin.value);
});

const handleSubmit = async updatedAssistant => {
  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      ...updatedAssistant,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// SiteUp (#6): clona o agente como template de outro nicho. Cria a cópia no
// backend, recarrega a lista e leva o usuário pra editar a cópia.
const handleClone = async () => {
  if (isCloning.value) return;

  isCloning.value = true;
  try {
    const { data } = await CaptainAssistantAPI.clone(assistantId.value);
    await store.dispatch('captainAssistants/get');
    useAlert('Agente clonado! Abrindo a cópia para você editar.');
    if (data?.id) {
      router.push({
        name: 'captain_assistants_settings_index',
        params: {
          accountId: route.params.accountId,
          assistantId: data.id,
        },
      });
    }
  } catch (error) {
    useAlert(error?.message || 'Não foi possível clonar o agente.');
  } finally {
    isCloning.value = false;
  }
};

const handleDelete = () => {
  deleteAssistantDialog.value.dialogRef.open();
};

const handleDeleteSuccess = () => {
  // Get remaining assistants after deletion
  const remainingAssistants = assistants.value.filter(
    a => a.id !== assistantId.value
  );

  if (remainingAssistants.length > 0) {
    // Navigate to the first available assistant's settings
    const nextAssistant = remainingAssistants[0];
    router.push({
      name: 'captain_assistants_settings_index',
      params: {
        accountId: route.params.accountId,
        assistantId: nextAssistant.id,
      },
    });
  } else {
    // No assistants left, redirect to create assistant page
    router.push({
      name: 'captain_assistants_create_index',
      params: { accountId: route.params.accountId },
    });
  }
};
</script>

<template>
  <PageLayout
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
    :class="{
      '[&>header>div]:max-w-[60rem] [&>main>div]:max-w-[60rem]':
        isCaptainV2Enabled,
    }"
  >
    <template #body>
      <div class="flex flex-col gap-8 pb-8">
        <!-- SiteUp: configurar com ajuda de IA (meta-prompt pra IA externa) -->
        <div
          class="flex items-center justify-between gap-4 rounded-2xl border border-n-weak bg-n-alpha-1 p-4"
        >
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="i-ri-sparkling-line size-5 shrink-0 mt-0.5 text-n-brand"
            />
            <div class="flex flex-col gap-1 min-w-0">
              <h6 class="text-n-slate-12 text-base font-medium">
                Não sabe por onde começar? Configure com ajuda de IA
              </h6>
              <span class="text-n-slate-11 text-sm">
                Pegue um meta-prompt pronto, cole numa IA (ChatGPT, Claude…) e
                receba o prompt principal, cenários, proteções e FAQs prontos
                para colar aqui.
              </span>
            </div>
          </div>
          <div class="flex-shrink-0">
            <Button
              label="Configurar com IA"
              icon="i-ri-sparkling-line"
              class="!w-fit"
              @click="handleConfigWithAi"
            />
          </div>
        </div>
        <OnboardingChecklist v-if="assistant" :assistant="assistant" />
        <span class="h-px w-full bg-n-weak" />
        <div class="flex flex-col gap-6">
          <SettingsHeader
            :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.BASIC_SETTINGS.TITLE')"
            :description="
              t('CAPTAIN.ASSISTANTS.SETTINGS.BASIC_SETTINGS.DESCRIPTION')
            "
          />
          <AssistantBasicSettingsForm
            :assistant="assistant"
            @submit="handleSubmit"
          />
        </div>
        <span class="h-px w-full bg-n-weak" />
        <div class="flex flex-col gap-6">
          <SettingsHeader
            :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.TITLE')"
            :description="
              t('CAPTAIN.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.DESCRIPTION')
            "
          />
          <AssistantSystemSettingsForm
            :assistant="assistant"
            @submit="handleSubmit"
          />
        </div>
        <span class="h-px w-full bg-n-weak" />
        <div class="flex flex-col gap-6">
          <SettingsHeader
            heading="Horário de resposta da IA"
            description="Defina se a IA responde 24h ou apenas em determinados dias e horários. Fora do horário ela fica em silêncio para o atendente humano assumir."
          />
          <AssistantScheduleSettingsForm
            :assistant="assistant"
            @submit="handleSubmit"
          />
        </div>
        <!-- Configurações avançadas: navegação para as sub-seções (V2) em grade -->
        <template v-if="isCaptainV2Enabled">
          <span class="h-px w-full bg-n-weak" />
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.TITLE')"
              :description="
                t('CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.DESCRIPTION')
              "
            />
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AssistantControlItems
                v-for="item in controlItems"
                :key="item.name"
                :control-item="item"
              />
            </div>
          </div>
        </template>
        <!-- Zona de ações: clonar/excluir, separada do conteúdo de configuração -->
        <span class="h-px w-full bg-n-weak" />
        <div class="flex flex-col gap-6">
          <SettingsHeader
            heading="Ações do agente"
            description="Clonar este agente como base para outro, ou removê-lo da conta."
          />
          <!-- SiteUp (#6): clonar o agente como template de outro nicho/cliente -->
          <div class="flex items-end justify-between w-full gap-4">
            <div class="flex flex-col gap-2">
              <h6 class="text-n-slate-12 text-base font-medium">
                Clonar agente
              </h6>
              <span class="text-n-slate-11 text-sm">
                Cria uma cópia deste agente — prompt, ferramentas, regras de
                campos, guardrails, agendas e cenários. Materiais e conhecimento
                treinado não são copiados; a cópia começa sem inbox vinculada.
              </span>
            </div>
            <div class="flex-shrink-0">
              <Button
                label="Clonar agente"
                color="slate"
                icon="i-ri-file-copy-line"
                class="max-w-56 !w-fit"
                :is-loading="isCloning"
                :disabled="isCloning"
                @click="handleClone"
              />
            </div>
          </div>
          <div class="flex items-end justify-between w-full gap-4">
            <div class="flex flex-col gap-2">
              <h6 class="text-n-slate-12 text-base font-medium">
                {{ t('CAPTAIN.ASSISTANTS.SETTINGS.DELETE.TITLE') }}
              </h6>
              <span class="text-n-slate-11 text-sm">
                {{ t('CAPTAIN.ASSISTANTS.SETTINGS.DELETE.DESCRIPTION') }}
              </span>
            </div>
            <div class="flex-shrink-0">
              <Button
                :label="
                  t('CAPTAIN.ASSISTANTS.SETTINGS.DELETE.BUTTON_TEXT', {
                    assistantName: assistant.name,
                  })
                "
                color="ruby"
                class="max-w-56 !w-fit"
                @click="handleDelete"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <DeleteDialog
      v-if="assistant"
      ref="deleteAssistantDialog"
      :entity="assistant"
      type="Assistants"
      translation-key="ASSISTANTS"
      @delete-success="handleDeleteSuccess"
    />
    <ConfigWithAiDialog
      v-if="assistant"
      ref="configWithAiDialog"
      :assistant="assistant"
    />
  </PageLayout>
</template>
